import type { IconComponent } from "../icon-types.ts";
import { ICON_NAMES, type IconName } from "../names.ts";
import { pickerIconGroups, pickerIconNames, pickerIcons } from "./generated/index.ts";

// The picker set: the glyphs a person can choose for an entity (a workflow,
// a team, a project type, a view). It is a separate set from
// the app-surface icons on purpose — a picked glyph never doubles as a
// control's glyph, so a workflow can never wear the map's or the settings'
// mark. Every name carries the `symbol-` prefix, which is what keeps the two
// sets from ever sharing a stored value.
export const PICKER_ICON_NAMES = pickerIconNames;
export type PickerIconName = (typeof PICKER_ICON_NAMES)[number];

export const PICKER_ICON_REGISTRY = pickerIcons satisfies Record<
  PickerIconName,
  IconComponent
>;

/** The picker's sections, in display order. */
export const PICKER_ICON_GROUPS: readonly {
  readonly id: string;
  readonly label: string;
  readonly names: readonly PickerIconName[];
}[] = pickerIconGroups;

const PICKER_ICON_NAME_SET: ReadonlySet<string> = new Set(PICKER_ICON_NAMES);

export function isPickerIconName(
  value: string | null | undefined,
): value is PickerIconName {
  return value != null && PICKER_ICON_NAME_SET.has(value);
}

export function toPickerIconName(
  value: string | null | undefined,
): PickerIconName | undefined {
  return isPickerIconName(value) ? value : undefined;
}

const PICKER_NAME_PREFIX = "symbol-";

/** The words a person searches and reads: `symbol-account-tree` → "account tree". */
export function pickerIconLabel(name: PickerIconName): string {
  return name.slice(PICKER_NAME_PREFIX.length).replace(/-/g, " ");
}

// A glyph name from EITHER set. This is the type of a stored icon column once
// it is read back: rows picked today hold picker names, and a column default
// or a row written before the sets split may still hold an app-surface name,
// which renders as itself rather than as the unknown-name fallback.
export type GlyphName = IconName | PickerIconName;

const GLYPH_NAME_SET: ReadonlySet<string> = new Set<string>([
  ...ICON_NAMES,
  ...PICKER_ICON_NAMES,
]);

export function isGlyphName(
  value: string | null | undefined,
): value is GlyphName {
  return value != null && GLYPH_NAME_SET.has(value);
}

export function toGlyphName(
  value: string | null | undefined,
): GlyphName | undefined {
  return isGlyphName(value) ? value : undefined;
}
